/**
 * The two ways into Case Automations: run your firm's automations, or run
 * case preparation (CaseBlink's nine workflows). Automations come first.
 * Only the chosen side is filled: Let CaseBlink Prepare in its lavender
 * soft accent (the empty case page's card), Run Your Automations in the
 * selected `fill-3`. The other side is a neutral card; unchosen, Prepare keeps
 * lavender only on its icon tile. `aria-checked` carries the choice.
 */
import { Sparkles, Zap, type LucideIcon } from "lucide-react";
import type { FC } from "react";

import { IconTile } from "@/components/ui/icon-tile";
import { cn } from "@/lib/utils";

export type BoardSource = "firm" | "caseblink";

type Option = {
  value: BoardSource;
  title: string;
  hint?: (automations: number) => string;
  icon: LucideIcon;
  /** The lavender soft-accent treatment when chosen, as on the empty case page. */
  accent?: boolean;
};

/** What the firm's automations option says it holds. */
export const automationsHint = (n: number) =>
  n
    ? `${n} ${n === 1 ? "automation" : "automations"} from your firm`
    : "Your firm's repeatable workflows";

const OPTIONS: readonly Option[] = [
  {
    value: "firm",
    title: "Run Your Automations",
    hint: automationsHint,
    icon: Zap
  },
  {
    value: "caseblink",
    title: "Let CaseBlink Prepare",
    icon: Sparkles,
    accent: true
  }
];

type Props = {
  value: BoardSource;
  onChange: (value: BoardSource) => void;
  /** How many automations the firm has for this case. */
  automations: number;
};

export const BoardEntry: FC<Props> = ({ value, onChange, automations }) => (
  <div
    role="radiogroup"
    aria-label="What to Run"
    className="grid gap-2 @lg:grid-cols-2"
    data-testid="board-entry"
  >
    {OPTIONS.map(({ value: option, title, hint, icon, accent }) => {
      const selected = option === value;
      return (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option)}
          data-testid={`board-entry-${option}`}
          data-state={selected ? "checked" : "unchecked"}
          data-variant={accent ? "accent" : undefined}
          className={cn(
            "flex min-h-14 items-center gap-3 rounded-xl px-3.5 py-2.5 text-left inset-ring transition-colors duration-100 outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            !selected &&
              "bg-elevated inset-ring-line-2 hover:bg-fill-1 hover:inset-ring-line-3",
            selected &&
              (accent
                ? "button-shimmer-accent bg-accent-soft inset-ring-accent-seed"
                : "bg-fill-3 inset-ring-line-4")
          )}
        >
          <IconTile
            size="lg"
            icon={icon}
            className={cn(
              accent
                ? cn(
                    "text-accent-seed",
                    selected ? "bg-accent-line" : "bg-accent-soft"
                  )
                : selected && "text-fg"
            )}
          />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-base font-medium text-fg">
              {title}
            </span>
            {hint ? (
              <span className="truncate text-sm text-fg-2">
                {hint(automations)}
              </span>
            ) : null}
          </span>
        </button>
      );
    })}
  </div>
);
